import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload } from 'lucide-react';

interface CreateChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChannelCreated?: (channelId: string, channelName: string) => void;
}

export const CreateChannelDialog = ({
  open,
  onOpenChange,
  onChannelCreated,
}: CreateChannelDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to create a channel',
        variant: 'destructive',
      });
      return;
    }

    if (!channelName.trim()) {
      toast({
        title: 'Channel Name Required',
        description: 'Please enter a channel name',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);

    try {
      const slug = generateSlug(channelName);

      // Check if slug already exists
      const { data: existingChannel } = await supabase
        .from('channels')
        .select('id')
        .eq('slug', slug)
        .single();

      if (existingChannel) {
        toast({
          title: 'Channel Already Exists',
          description: 'A channel with this name already exists. Please choose a different name.',
          variant: 'destructive',
        });
        setCreating(false);
        return;
      }

      // Upload logo if provided
      let logoUrl = null;
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop();
        const fileName = `${slug}-logo-${Date.now()}.${fileExt}`;
        const filePath = `channel-logos/${fileName}`;

        const { error: uploadError, data } = await supabase.storage
          .from('knowledge-bases')
          .upload(filePath, logoFile);

        if (uploadError) {
          console.error('Logo upload error:', uploadError);
          toast({
            title: 'Logo Upload Failed',
            description: 'Could not upload logo. Channel will be created without a logo.',
          });
        } else {
          const { data: { publicUrl } } = supabase.storage
            .from('knowledge-bases')
            .getPublicUrl(filePath);
          logoUrl = publicUrl;
        }
      }

      // Create channel
      const { data: newChannel, error: channelError } = await supabase
        .from('channels')
        .insert({
          name: channelName.trim(),
          slug,
          is_private: isPrivate,
        })
        .select()
        .single();

      if (channelError) throw channelError;

      // Add creator to the channel's user_organizations
      const { error: orgError } = await supabase
        .from('user_organizations')
        .insert({
          user_id: user.id,
          channel_id: newChannel.id,
          organization_name: channelName.trim(),
          is_primary: false,
        });

      if (orgError) {
        console.error('Error adding user to channel:', orgError);
      }

      toast({
        title: 'Channel Created',
        description: `${channelName} has been successfully created!`,
      });

      // Reset form
      setChannelName('');
      setDescription('');
      setIsPrivate(false);
      setLogoFile(null);
      setLogoPreview(null);
      
      if (onChannelCreated) {
        onChannelCreated(newChannel.id, newChannel.name);
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating channel:', error);
      toast({
        title: 'Error',
        description: 'Failed to create channel. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Channel</DialogTitle>
          <DialogDescription>
            Set up a new channel for organizing knowledge bases and collaborating with others.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label htmlFor="channelName">Channel Name *</Label>
            <Input
              id="channelName"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="e.g., Engineering Team"
              required
            />
            <p className="text-sm text-muted-foreground">
              URL slug will be: {channelName ? generateSlug(channelName) : '...'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this channel is for..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="logo">Channel Logo (Optional)</Label>
            <div className="flex items-center gap-4">
              {logoPreview && (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-16 h-16 object-cover rounded border"
                />
              )}
              <div className="flex-1">
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="cursor-pointer"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Upload a logo image for your channel
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="space-y-0.5">
              <Label htmlFor="isPrivate">Private Channel</Label>
              <p className="text-sm text-muted-foreground">
                Only invited members can access this channel
              </p>
            </div>
            <Switch
              id="isPrivate"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> After creating the channel, you can add industry domains and
              invite team members from the channel settings.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={creating}>
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Channel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
